<template>
    <TabPane
        emptyTitle="Apply a Tag"
        emptyBody="To get started, add a tag."
        emptyButton="Add a Tag"
        :pending="pending"
        :error="error"
        :count="count"
        @emptyClick="adding = true"
    >

        <template v-slot:icon>
            <h3 class="card__title card__title--group">Manage Applied Tags</h3>
        </template>

        <template v-slot:buttons>
            <fe-btn usage="ghost" :disabled="!success && !error" text icon @click="reset">
                <v-icon>refresh</v-icon>
            </fe-btn>
        </template>

        <template v-slot:content>
            <fe-grid
                ref="grid"
                domLayout="autoHeight"
                :showToolbar="false"
                :columnDefs="columnDefs"
                :rowData="rowData"
                :styles="{ height: null }"
                @rowDataChanged="onSelectionChanged"
                @selectionChanged="onSelectionChanged">
            </fe-grid>

            <fe-btn class="mt-5" usage="tertiary" @click="adding = true">Add Tags</fe-btn>
        </template>

        <template v-slot:modals>
            <TagSelector
                :active="adding"
                :hidden-ids="existingTagIds"
                @apply="addTags"
                @cancel="adding = false"
            />

            <DeleteFooter
                modelName="applied tag"
                :selectedIndexes="selectedIndexes"
                :items="rowData"
                @selectAll="() => $refs.grid.selectAll()"
                @deselectAll="() => $refs.grid.deselectAll()"
                @deleteSelection="removeTags"
            />
        </template>
    </TabPane>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import TagSelector from '../modals/TagSelector'
import TabPane from '../templates/TabPane'
import DeleteFooter from '../templates/DeleteFooter'

export default {
    name: 'Tags',

    components: {
        TagSelector,
        TabPane,
        DeleteFooter
    },

    data() {
        return {
            selectedIndexes: [],
            adding: false
        }
    },

    computed: {
        ...mapGetters('thresholds/group/tag', [
            'collection',
            'clear',
            'pending',
            'success',
            'error',
            'hasDirtied',
            'hasRemoved',
            'hasAdded',
            'hasChanges',
            'rowData',
            'count'
        ]),

        thresholdGroupId() {
            return this.$store.getters['thresholds/group/selected']?.id
        },

        performanceBands() {
            return this.$store.getters['thresholds/group/selected']?.performance_band || []
        },

        columnDefs() {
            return [
                {headerName: null, headerCheckboxSelection: true, checkboxSelection: true, width: 70, minWidth: 70},
                {headerName: 'Tag', field: 'tag_name', sortable: true},
                {
                    headerName: 'Performance Band',
                    field: 'threshold_performance_band_id',
                    sortable: true,
                    editable: true,
                    cellRendererFramework: 'FeGridChipSelect',
                    cellRendererParams: {
                        rowDataKey: 'threshold_performance_band_id',
                        mode: 'render',
                        multiple: true,
                        items: this.performanceBands,
                        keyProp: 'id',
                        labelProp: 'name'
                    },
                    cellEditorFramework: 'FeGridChipSelect',
                    cellEditorParams: {
                        rowDataKey: 'threshold_performance_band_id',
                        mode: 'edit',
                        multiple: true,
                        items: this.performanceBands,
                        keyProp: 'id',
                        labelProp: 'name'
                    },
                    onCellValueChanged: this.updateCell,
                    cellStyle: {padding: 0}
                },
                {headerName: 'Created', field: 'created', sortable: true}
            ]
        },

        hasSelection() {
            return (this.selectedIndexes.length > 0)
        },

        existingTagIds() {
            return this.collection.map(itm => itm.tag_id)
        }
    },

    watch: {
        clear: (v) => {
            if (v) this.reset()
        },
        performanceBands: {
            handler() {
                this.$refs?.grid.api.refreshCells()
            },
            deep: true
        }
    },

    mounted() {
        this.reset()
    },

    updated() {
        if (this.clear) this.reset()
    },

    methods: {
        ...mapActions('thresholds/group/tag', [
            'refresh',
            'add',
            'remove',
            'update',
            'reconcile'
        ]),

        updateCell(params) {
            this.update(params.data)
            this.$refs.grid.resize()
        },

        onSelectionChanged(params) {
            this.selectedIndexes = (params.api)
                ? params.api.getSelectedRows().map(itm => itm.__idx)
                : []
        },

        addTags(tags) {
            this.add(tags.map(tag => {
                return {
                    threshold_group_id: this.thresholdGroupId,
                    tag_name: tag.name,
                    tag_id: tag.id,
                    threshold_performance_band_id: []
                }
            }))
            this.adding = false
        },

        removeTags() {
            this.remove({indexes: this.selectedIndexes})
            this.selectedIndexes = []
        },

        reset() {
            this.refresh()
            this.selectedIndexes = []
        },

        save() {
            this.reconcile()
        }
    }
}
</script>
